import { z } from 'zod'
import { apiCall } from './index'
import { getCurrentUserId, getCurrentUserName } from './unified-auth'
import { countWords } from '../lib/utils'

// Core types
export interface ManualRewardPointsRequest {
  firstName: string
  lastName: string
  email: string
  points: number
  rewardedById: number
  rewardedByName: string
  eventType: string
  fundedByDepartmentId: number
  isEntrataEmployee: boolean
  employeeEmail?: string
  description: string
}

export interface ManualRewardPointsResponse {
  success: boolean
  message: string
  transactionId?: string
  pointsAwarded?: number
}

export interface BulkRewardPointsRequest {
  csvFile: File
  rewardedById: number
  rewardedByName: string
  eventType: string
}

export interface BulkRewardPointsResponse {
  message: string
  processed: number
}

export interface TransactionHistoryRequest {
  fromDate?: string
  toDate?: string
}

export interface TransactionHistoryItem {
  id: number
  rewardEventTypeId: number
  rewardEventStatusId: number
  points: number
  createdDate: string
  emailAddress: string
  firstName: string
  lastName: string
  homebodyUUID: string
  rewardedById: number | null
  rewardedByName: string | null
  fundedByDepartmentId: number | null
  description: string
  isEntrataEmployee: boolean
  employeeEmail: string | null
  rewardEventType: string
  rewardEventStatus: string
}

// Validation schemas
const ManualRewardPointsRequestSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  points: z.number().min(1, 'Points must be at least 1').max(10000, 'Points cannot exceed 10000'),
  rewardedById: z.number().positive('Rewarded by ID must be a positive number'),
  rewardedByName: z.string().min(1, 'Rewarded by name is required'),
  eventType: z.string().min(1, 'Event type is required'),
  fundedByDepartmentId: z.number().positive('Department ID must be a positive number'),
  isEntrataEmployee: z.boolean(),
  employeeEmail: z.string().email('Valid employee email is required').optional(),
  description: z.string().refine(val => countWords(val) <= 500, {
    message: 'Description must be 500 words or less',
  }),
})

const BulkRewardPointsRequestSchema = z.object({
  csvFile: z.instanceof(File, { message: 'CSV file is required' }),
  rewardedById: z.number().positive('Rewarded by ID must be a positive number'),
  rewardedByName: z.string().min(1, 'Rewarded by name is required'),
  eventType: z.string().min(1, 'Event type is required'),
})

const TransactionHistoryRequestSchema = z.object({
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
})

// Helper function to handle API responses
async function handleApiResponse(response: Response) {
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
  }
  return response.json()
}

// API functions
export async function addManualRewardPoints(
  data: ManualRewardPointsRequest
): Promise<ManualRewardPointsResponse> {
  const validatedData = ManualRewardPointsRequestSchema.parse(data)

  const response = await apiCall('/v1/rewards/manual-reward-points', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(validatedData),
  })

  return handleApiResponse(response)
}

export async function addBulkRewardPoints(
  data: BulkRewardPointsRequest
): Promise<BulkRewardPointsResponse> {
  const validatedData = BulkRewardPointsRequestSchema.parse(data)

  const formData = new FormData()
  formData.append('file', validatedData.csvFile)
  formData.append('rewardedById', validatedData.rewardedById.toString())
  formData.append('rewardedByName', validatedData.rewardedByName)
  formData.append('eventType', validatedData.eventType)

  const response = await apiCall('/v1/rewards/manual-bulk-reward-points', {
    method: 'POST',
    body: formData,
  })

  return handleApiResponse(response)
}

export async function getTransactionHistory(
  data?: TransactionHistoryRequest
): Promise<TransactionHistoryItem[]> {
  const validatedData = data ? TransactionHistoryRequestSchema.parse(data) : {}
  const requestBody =
    Object.keys(validatedData).length > 0 ? JSON.stringify(validatedData) : undefined

  const response = await apiCall('/v1/rewards/manual-rewards-transaction-history', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: requestBody,
  })

  return handleApiResponse(response)
}

// Helper function to create manual reward request
export function createDefaultManualRewardRequest(
  firstName: string,
  lastName: string,
  email: string,
  points: number,
  description: string,
  isEntrataEmployee: boolean = false,
  employeeEmail: string = '',
  departmentId: number = 23
): ManualRewardPointsRequest {
  return {
    firstName,
    lastName,
    email,
    points,
    rewardedById: getCurrentUserId(),
    rewardedByName: getCurrentUserName(),
    eventType: 'ENTRATA_REWARDS',
    fundedByDepartmentId: departmentId,
    isEntrataEmployee,
    ...(isEntrataEmployee && { employeeEmail: employeeEmail }),
    description,
  }
}

// Helper function to create bulk reward request
export function createBulkRewardRequest(csvFile: File): BulkRewardPointsRequest {
  return {
    csvFile,
    rewardedById: getCurrentUserId(),
    rewardedByName: getCurrentUserName(),
    eventType: 'ENTRATA_REWARDS',
  }
}
